/**
 * CRM ↔ Blast vocabulary translation for the Convert flow.
 *
 * CRM stores Sport.gender as 'BOYS' | 'GIRLS' | 'COED' (Prisma enum).
 * CRM stores Team.level as freeform text (rep types it in AddTeamModal).
 * The Blast convert API and ConvertToBlastModal use codes — these helpers
 * translate.
 */

export type BlastGenderCode = 'm' | 'w' | 'mw';
export type BlastLevelCode =
  | 'varsity'
  | 'jrvarsity'
  | 'freshman'
  | 'froshsoph'
  | 'sophomore'
  | 'program';

export function mapCrmGenderToBlastGender(crmGender: 'BOYS' | 'GIRLS' | 'COED'): BlastGenderCode {
  switch (crmGender) {
    case 'BOYS': return 'm';
    case 'GIRLS': return 'w';
    case 'COED': return 'mw';
  }
}

/**
 * Maps a freeform CRM team level to a Blast level code.
 * Case-insensitive, tolerates common punctuation/spacing variants.
 * Unknown levels fall back to 'varsity' — the rep can change it in the modal.
 */
export function mapCrmLevelToBlastLevel(crmLevel: string): BlastLevelCode {
  const normalized = crmLevel.trim().toLowerCase().replace(/[\s.]+/g, '');
  // 'Jr. Varsity' → 'jrvarsity', 'Jr Varsity' → 'jrvarsity'
  // 'Junior Varsity' → 'juniorvarsity'
  // 'Frosh/Soph' → 'frosh/soph' (slash preserved) → handled below
  if (normalized === 'varsity') return 'varsity';
  if (normalized === 'jv' || normalized === 'jrvarsity' || normalized === 'juniorvarsity') return 'jrvarsity';
  if (normalized === 'freshman' || normalized === 'fresh') return 'freshman';
  if (normalized === 'frosh/soph' || normalized === 'froshsoph' || normalized === 'frosh-soph') return 'froshsoph';
  if (normalized === 'sophomore' || normalized === 'soph') return 'sophomore';
  if (normalized === 'program') return 'program';
  return 'varsity';
}

/**
 * Display label for a Blast gender CODE. Co-ed ('mw') yields '' (no word) — see CRM-25.
 * Mirrors the CRM-enum genderLabel() in lib/sportLabel.ts.
 */
export function blastGenderLabel(code: string): string {
  if (code === 'm') return 'Boys';
  if (code === 'w') return 'Girls';
  return '';
}
